import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ChevronRight, ChevronLeft, BookOpen, Zap, Sparkles, Check, ArrowLeft, Trophy, Menu } from 'lucide-react';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { useCredits } from '../hooks/useCredits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The Acropolis — full-screen distraction-free study zone
export default function AcropolisZone({ onExit, initialSubject, initialChapter }) {
    const { user } = useOnboarding();
    const { spend, out, grant } = useCredits();
    const profile = useMemo(() => ({
        name: user?.name,
        board_class: user?.board_class || 'CBSE Class 10',
        class_level: user?.class_level,
        primary_exam_track: user?.primary_exam_track,
        additional_national_exams: user?.additional_national_exams || [],
        subscription_tier: user?.subscription_tier,
        focus_areas: user?.focus_areas || [],
        primary_objective: user?.primary_objective,
        time_commitment: user?.time_commitment,
    }), [user]);

    const [catalog, setCatalog] = useState({});
    const [subject, setSubject] = useState('');
    const [chapter, setChapter] = useState('');
    const [view, setView] = useState('lesson'); // 'lesson' | 'quiz'

    const [lesson, setLesson] = useState(null);
    const [lessonLoading, setLessonLoading] = useState(false);
    const [conceptIdx, setConceptIdx] = useState(0);

    const [quiz, setQuiz] = useState(null);
    const [quizLoading, setQuizLoading] = useState(false);
    const [answers, setAnswers] = useState({});
    const [scored, setScored] = useState(false);

    // Retractable sidebar — default open on desktop, closed on smaller screens
    const [sidebarOpen, setSidebarOpen] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
    );

    // ESC to exit
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onExit?.(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onExit]);

    useEffect(() => {
        axios.get(`${API}/acropolis/catalog`).then((r) => {
            setCatalog(r.data?.subjects || {});
            // preselect first focus subject if the student picked any
            const preferred = (profile.focus_areas || []).find((s) => r.data?.subjects?.[s]);
            const first = preferred || Object.keys(r.data?.subjects || {})[0];
            if (first) setSubject(first);
        }).catch((e) => console.error(e));
    }, []); // eslint-disable-line

    const chaptersForSubject = catalog[subject] || [];

    const loadLesson = useCallback(async (subj, chap) => {
        if (!subj || !chap) return;
        if (out || !spend(1)) { setLesson({ error: 'You\u2019re out of credits — tap Buy more credits to keep learning.' }); return; }
        setLessonLoading(true);
        setLesson(null);
        setConceptIdx(0);
        setQuiz(null);
        setScored(false);
        setAnswers({});
        setView('lesson');
        try {
            const r = await axios.post(`${API}/tutor/lesson`, {
                subject: subj, chapter: chap, board_class: profile.board_class, profile,
            });
            setLesson(r.data);
        } catch (e) {
            grant(1); // refund — lesson failed
            const quota = e?.response?.data?.detail === 'metis_quota_exhausted';
            setLesson({ error: quota
                ? 'Metis AI quota is temporarily exhausted (billing). Your credit was refunded — please try again soon.'
                : 'Metis could not draft this lesson. Your credit was refunded — try another chapter.' });
        } finally {
            setLessonLoading(false);
        }
    }, [profile, spend, out, grant]);

    // Deep link from header Smart Search: jump straight into a chapter lesson.
    // Works for any board/stream (lesson endpoint is generic), not just the CBSE catalog.
    const initialLoaded = useRef(false);
    useEffect(() => {
        if (initialLoaded.current || !initialChapter) return;
        initialLoaded.current = true;
        const subj = initialSubject || subject;
        if (subj) setSubject(subj);
        setChapter(initialChapter);
        loadLesson(subj, initialChapter);
    }, [initialSubject, initialChapter, subject, loadLesson]);

    async function loadQuiz() {
        if (!chapter) return;
        if (out || !spend(1)) { setQuiz({ error: 'You\u2019re out of credits — tap Buy more credits to keep learning.' }); setView('quiz'); return; }
        setQuizLoading(true); setQuiz(null); setScored(false); setAnswers({});
        setView('quiz');
        try {
            const r = await axios.post(`${API}/tutor/quiz`, {
                goal: `NCERT chapter revision: ${chapter}`,
                minutes: 10,
                level: profile.board_class || 'CBSE Class 10',
                board_class: profile.board_class,
                focus_areas: [subject],
                primary_objective: profile.primary_objective,
                time_commitment: profile.time_commitment,
            });
            setQuiz(r.data);
        } catch (e) {
            grant(1); // refund — quiz failed
            const quota = e?.response?.data?.detail === 'metis_quota_exhausted';
            setQuiz({ error: quota
                ? 'Metis AI quota is temporarily exhausted (billing). Your credit was refunded — please try again soon.'
                : 'Speed test unavailable — your credit was refunded. Try again.' });
        } finally {
            setQuizLoading(false);
        }
    }

    function submitQuiz() {
        if (!quiz?.questions) return;
        setScored(true);
    }

    function score() {
        if (!quiz?.questions) return 0;
        return quiz.questions.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0);
    }

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex flex-col"
            data-testid="acropolis-zone"
            style={{
                background:
                    'radial-gradient(70% 60% at 20% 10%, rgba(59,68,30,0.98) 0%, #0B0F07 55%, #050702 100%)',
            }}
        >
            {/* Header */}
            <header className="flex items-center justify-between px-4 sm:px-6 lg:px-10 py-4 border-b border-white/15">
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={() => setSidebarOpen((v) => !v)}
                        data-testid="acropolis-sidebar-toggle"
                        aria-label={sidebarOpen ? 'Hide chapters' : 'Show chapters'}
                        className="h-9 w-9 rounded-sm flex items-center justify-center border border-white/30 text-white hover:bg-white/10 hover:border-[#D4B3FF]/70 transition-colors"
                    >
                        <Menu size={16} />
                    </button>
                    <button
                        onClick={onExit}
                        data-testid="acropolis-exit"
                        className="flex items-center gap-1 mono text-[10px] text-white hover:text-[#D4B3FF]"
                    >
                        <ArrowLeft size={12} /> EXIT
                    </button>
                    <span className="text-[#9CA3AF] hidden sm:inline">·</span>
                    <div className="mono text-[10px] tracking-[0.3em] text-white hidden sm:block">
                        THE ACROPOLIS
                    </div>
                </div>
                <div className="mono text-[9px] sm:text-[10px] text-[#D1D5DB] text-right">
                    {profile.board_class} <span className="hidden sm:inline">· Distraction-free study</span>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden relative">
                {/* Backdrop for sidebar on mobile / tablet — closes on tap */}
                <AnimatePresence>
                    {sidebarOpen && (
                        <motion.div
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/40 z-10 lg:hidden"
                            onClick={() => setSidebarOpen(false)}
                            data-testid="acropolis-sidebar-backdrop"
                        />
                    )}
                </AnimatePresence>

                {/* Left rail: subjects + chapters (retractable across ALL breakpoints) */}
                <AnimatePresence initial={false}>
                    {sidebarOpen && (
                        <motion.aside
                            key="sidebar"
                            initial={{ x: -320, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -320, opacity: 0 }}
                            transition={{ duration: 0.32, ease: [0.83, 0, 0.17, 1] }}
                            className="absolute lg:relative z-20 w-72 sm:w-80 h-full border-r border-white/15 overflow-y-auto py-4 book-scroll"
                            style={{
                                background: 'linear-gradient(180deg, #0B0F07 0%, #050702 100%)',
                                willChange: 'transform',
                            }}
                            data-testid="acropolis-sidebar"
                        >
                    <div className="px-5 mono text-[10px] font-semibold tracking-[0.24em] text-[#E9DFFF] mb-2">SUBJECTS</div>
                    <div className="px-3 space-y-1 mb-5">
                        {Object.keys(catalog).map((s) => {
                            const active = s === subject;
                            return (
                                <button
                                    key={s}
                                    onClick={() => { setSubject(s); setChapter(''); setLesson(null); setQuiz(null); }}
                                    data-testid={`acropolis-subject-${s.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`w-full text-left px-3 py-1.5 rounded-sm text-[13px] transition-colors ${
                                        active
                                            ? 'bg-[#B79CFF]/30 text-white font-semibold ring-1 ring-[#D4B3FF]/70'
                                            : 'text-[#E5E7EB] hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                    <div className="px-5 mono text-[10px] font-semibold tracking-[0.24em] text-[#E9DFFF] mb-2">
                        CHAPTERS · {chaptersForSubject.length}
                    </div>
                    <div className="px-3 space-y-1">
                        {chaptersForSubject.map((c) => {
                            const active = c === chapter;
                            return (
                                <button
                                    key={c}
                                    onClick={() => {
                                        setChapter(c);
                                        loadLesson(subject, c);
                                        // Auto-close overlay sidebar on non-desktop for a cleaner reading canvas
                                        if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 1024px)').matches) {
                                            setSidebarOpen(false);
                                        }
                                    }}
                                    data-testid={`acropolis-chapter-${c.slice(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`w-full text-left px-3 py-2 rounded-sm text-[12.5px] leading-snug transition-colors flex items-start gap-2 ${
                                        active
                                            ? 'bg-[#B79CFF]/40 text-white font-medium ring-1 ring-[#D4B3FF]/80'
                                            : 'text-[#E5E7EB] hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    <span className={`mono text-[10px] pt-[2px] ${active ? 'text-[#E4CDFF]' : 'text-[#CBB4FF]'}`}>
                                        {String(chaptersForSubject.indexOf(c) + 1).padStart(2, '0')}
                                    </span>
                                    <span className="flex-1">{c}</span>
                                </button>
                            );
                        })}
                    </div>
                        </motion.aside>
                    )}
                </AnimatePresence>

                {/* Center: lesson or quiz */}
                <main className="flex-1 overflow-y-auto px-6 lg:px-12 py-8" data-testid="acropolis-main">
                    {/* View toggle */}
                    <div className="flex items-center gap-3 mb-6">
                        <button
                            onClick={() => setView('lesson')}
                            data-testid="acropolis-tab-lesson"
                            className={`px-4 py-1.5 rounded-full mono text-[10px] flex items-center gap-1.5 transition-colors ${
                                view === 'lesson'
                                    ? 'bg-[#B79CFF]/25 text-[#D4B3FF] border border-[#B79CFF]/50'
                                    : 'text-[#D1D5DB] border border-white/20 hover:text-white'
                            }`}
                        >
                            <BookOpen size={11} /> Lesson
                        </button>
                        <button
                            onClick={() => (view === 'quiz' ? null : loadQuiz())}
                            disabled={!chapter}
                            data-testid="acropolis-tab-quiz"
                            className={`px-4 py-1.5 rounded-full mono text-[10px] flex items-center gap-1.5 transition-colors disabled:opacity-40 ${
                                view === 'quiz'
                                    ? 'bg-[#B79CFF]/25 text-[#D4B3FF] border border-[#B79CFF]/50'
                                    : 'text-[#D1D5DB] border border-white/20 hover:text-white'
                            }`}
                        >
                            <Zap size={11} /> 10-min Speed Test
                        </button>
                        {chapter && (
                            <div className="ml-auto mono text-[10px] text-[#D1D5DB]">
                                {subject} · {chapter}
                            </div>
                        )}
                    </div>

                    <AnimatePresence mode="wait">
                        {!chapter ? (
                            <EmptyPrompt key="empty" profile={profile} />
                        ) : view === 'lesson' ? (
                            <LessonView
                                key={'lesson-' + chapter}
                                lesson={lesson}
                                loading={lessonLoading}
                                conceptIdx={conceptIdx}
                                setConceptIdx={setConceptIdx}
                                onStartQuiz={loadQuiz}
                                chapter={chapter}
                                subject={subject}
                            />
                        ) : (
                            <QuizView
                                key={'quiz-' + chapter}
                                quiz={quiz}
                                loading={quizLoading}
                                answers={answers}
                                setAnswers={setAnswers}
                                scored={scored}
                                submit={submitQuiz}
                                score={score()}
                                onBackToLesson={() => setView('lesson')}
                                onRetry={loadQuiz}
                            />
                        )}
                    </AnimatePresence>
                </main>
            </div>
        </motion.div>,
        document.body
    );
}

function EmptyPrompt({ profile }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center pt-16"
            data-testid="acropolis-empty"
        >
            <div className="inline-flex items-center gap-2 mono text-[10px] text-[#D4B3FF] mb-4">
                <Sparkles size={12} /> METIS AWAITS
            </div>
            <h2 className="display text-white" style={{ fontSize: 'clamp(34px, 4vw, 56px)', lineHeight: 1.05 }}>
                Pick a subject and a chapter.
                <br />
                <span className="display-italic text-[#D4B3FF]">I'll walk you through it, one concept at a time.</span>
            </h2>
            <div className="mono text-[11px] text-[#D1D5DB] mt-6">
                {profile.focus_areas?.length
                    ? `You picked ${profile.focus_areas.join(' · ')} at onboarding — start there.`
                    : 'Full CBSE Class 10 NCERT catalog available on the left.'}
            </div>
        </motion.div>
    );
}

function LessonView({ lesson, loading, conceptIdx, setConceptIdx, onStartQuiz, chapter, subject }) {
    if (loading) {
        return (
            <motion.div key="l-load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mono text-[11px] text-[#D1D5DB]" data-testid="acropolis-lesson-loading">
                Metis is drafting the lesson for “{chapter}”…
            </motion.div>
        );
    }
    if (lesson?.error) return <div className="text-[#D1D5DB] text-sm">{lesson.error}</div>;
    if (!lesson?.concepts?.length) return null;
    const concepts = lesson.concepts;
    const at = concepts[conceptIdx];
    const done = conceptIdx >= concepts.length - 1;

    return (
        <motion.div
            key="lesson"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
            data-testid="acropolis-lesson"
        >
            {lesson.intro && (
                <div className="text-[#E5E7EB] text-[15px] mb-6 border-l-2 border-[#B79CFF]/70 pl-4 italic">
                    {lesson.intro}
                </div>
            )}
            <div className="flex items-center gap-1.5 mb-4" data-testid="lesson-progress">
                {concepts.map((_, i) => (
                    <div key={i} className={`h-[3px] flex-1 rounded-full ${i <= conceptIdx ? 'bg-[#B79CFF]' : 'bg-white/15'}`} />
                ))}
            </div>
            <motion.div
                key={conceptIdx}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                className="rounded-lg border border-white/20 bg-white/5 p-6"
                data-testid="acropolis-concept-card"
            >
                <div className="mono text-[10px] text-[#D4B3FF] mb-2">
                    CONCEPT {conceptIdx + 1} / {concepts.length}
                </div>
                <h3 className="display text-white text-2xl mb-3" data-testid="concept-title">
                    {at.title}
                </h3>
                <p className="text-[#F3F4F6] text-[15px] leading-relaxed mb-4" data-testid="concept-explain">
                    {at.explain}
                </p>
                {at.example && (
                    <div className="mono text-[11px] text-[#D1D5DB] mb-4 pl-3 border-l border-white/25">
                        <span className="text-[#D4B3FF]">EXAMPLE — </span>
                        {at.example}
                    </div>
                )}
                {at.check && (
                    <div className="rounded-sm border border-[#B79CFF]/50 bg-[#B79CFF]/10 px-4 py-3">
                        <div className="mono text-[9px] text-[#D4B3FF] mb-1">SOCRATIC CHECK</div>
                        <div className="text-white text-[14px]">{at.check}</div>
                        <button
                            onClick={() => window.dispatchEvent(new CustomEvent('metis:open', { detail: { prompt: `Explain "${at.title}" from ${subject} → ${chapter}. Answer this check: ${at.check}` } }))}
                            className="mt-2 mono text-[10px] text-[#D4B3FF] hover:underline flex items-center gap-1"
                            data-testid="ask-metis"
                        >
                            <Sparkles size={11} /> Ask Metis
                        </button>
                    </div>
                )}
            </motion.div>

            <div className="flex items-center justify-between mt-6">
                <button
                    onClick={() => setConceptIdx((i) => Math.max(0, i - 1))}
                    disabled={conceptIdx === 0}
                    data-testid="concept-prev"
                    className="mono text-[11px] text-[#D1D5DB] hover:text-white disabled:opacity-30 flex items-center gap-1"
                >
                    <ChevronLeft size={13} /> Prev
                </button>
                {!done ? (
                    <button
                        onClick={() => setConceptIdx((i) => Math.min(concepts.length - 1, i + 1))}
                        data-testid="concept-next"
                        className="btn-lavender px-5 py-2 rounded-sm text-[11px] mono flex items-center gap-1"
                    >
                        Next <ChevronRight size={13} />
                    </button>
                ) : (
                    <button
                        onClick={onStartQuiz}
                        data-testid="concept-take-quiz"
                        className="btn-lavender px-5 py-2 rounded-sm text-[11px] mono flex items-center gap-1"
                    >
                        <Zap size={13} /> Take the 10-min quiz
                    </button>
                )}
            </div>

            {lesson.closing && done && (
                <div className="mt-6 text-[#D1D5DB] text-[13px] italic border-t border-white/15 pt-4">
                    {lesson.closing}
                </div>
            )}
        </motion.div>
    );
}

function QuizView({ quiz, loading, answers, setAnswers, scored, submit, score, onBackToLesson, onRetry }) {
    if (loading) {
        return (
            <motion.div key="q-load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mono text-[11px] text-[#D1D5DB]" data-testid="acropolis-quiz-loading">
                Metis is loading your 10-minute speed test…
            </motion.div>
        );
    }
    if (quiz?.error) return <div className="text-[#D1D5DB] text-sm">{quiz.error}</div>;
    if (!quiz?.questions?.length) return <div className="text-[#D1D5DB] text-sm">No quiz loaded yet — pick a chapter first.</div>;

    return (
        <motion.div key="quiz" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto" data-testid="acropolis-quiz">
            <div className="mono text-[10px] text-[#D4B3FF] mb-1">QUIZ · {quiz.topic}</div>
            {quiz.questions.map((q, i) => (
                <div key={i} className="border-b border-white/15 py-4">
                    <div className="text-white text-[15px] mb-2" data-testid={`ac-quiz-q-${i}`}>
                        {i + 1}. {q.q}
                    </div>
                    <div className="grid md:grid-cols-2 gap-2">
                        {q.opts.map((o, oi) => {
                            const chosen = answers[i] === oi;
                            const showCorrect = scored && oi === q.answer;
                            const showWrong = scored && chosen && oi !== q.answer;
                            return (
                                <button
                                    key={oi} disabled={scored}
                                    onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                                    data-testid={`ac-quiz-${i}-opt-${oi}`}
                                    className={`text-left px-3 py-2 rounded-sm border text-[13px] transition-all ${
                                        showCorrect ? 'bg-[#7B5EE0]/50 border-[#B79CFF] text-white' :
                                        showWrong ? 'bg-red-500/20 border-red-400/70 text-white' :
                                        chosen ? 'bg-[#B79CFF]/20 border-[#B79CFF] text-white' :
                                        'border-white/25 bg-white/5 text-[#F3F4F6] hover:border-white/50'
                                    }`}
                                >
                                    <span className="mono text-[9px] text-[#9CA3AF] mr-2">
                                        {String.fromCharCode(65 + oi)}
                                    </span>
                                    <span>{o}</span>
                                </button>
                            );
                        })}
                    </div>
                    {scored && (
                        <div className="mono text-[10px] text-[#D1D5DB] mt-2">
                            ✎ {q.explain}
                        </div>
                    )}
                </div>
            ))}

            {!scored ? (
                <div className="flex items-center justify-between mt-6">
                    <button onClick={onBackToLesson} className="mono text-[11px] text-[#D1D5DB] hover:text-white" data-testid="back-to-lesson">
                        ← Back to lesson
                    </button>
                    <button
                        onClick={submit}
                        disabled={Object.keys(answers).length === 0}
                        data-testid="ac-quiz-submit"
                        className="btn-lavender px-5 py-2 rounded-sm text-[11px] mono disabled:opacity-40"
                    >
                        Submit answers
                    </button>
                </div>
            ) : (
                <div className="mt-6 rounded-sm border border-[#B79CFF]/50 bg-[#B79CFF]/10 p-5 flex items-center gap-4" data-testid="ac-quiz-result">
                    <Trophy size={22} className="text-[#D4B3FF]" />
                    <div className="flex-1">
                        <div className="display text-white text-2xl">
                            {score} / {quiz.questions.length} correct
                        </div>
                        <div className="mono text-[10px] text-[#D1D5DB] mt-1">
                            {score === quiz.questions.length ? 'A perfect run — Metis is proud.' :
                             score >= quiz.questions.length * 0.7 ? 'Solid. Chapter concepts locking in.' :
                             'Revisit the concept cards — you\u2019re closer than you think.'}
                        </div>
                    </div>
                    <button onClick={onRetry} data-testid="ac-quiz-retry" className="px-4 py-2 rounded-sm text-[11px] mono border border-white/35 text-white hover:bg-white/10">
                        Retry
                    </button>
                    <button onClick={onBackToLesson} data-testid="ac-quiz-back" className="btn-lavender px-4 py-2 rounded-sm text-[11px] mono">
                        Back to lesson
                    </button>
                </div>
            )}
        </motion.div>
    );
}
